import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, SearchIcon, CalendarIcon, MusicIcon, MessageSquareIcon, MenuIcon, XIcon, ChevronsLeftIcon, ChevronsRightIcon } from 'lucide-react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { useTheme } from '../providers/ThemeProvider';
interface StudentLayoutProps {
  children: React.ReactNode;
}
const COLLAPSED_WIDTH = 72;
const EXPANDED_WIDTH = 256;
const navItems = [{
  to: '/student-dashboard',
  icon: HomeIcon,
  label: 'Dashboard'
}, {
  to: '/find-teachers',
  icon: SearchIcon,
  label: 'Find Teachers'
}, {
  to: '/student-schedule',
  icon: CalendarIcon,
  label: 'My Schedule'
}, {
  to: '/sheet-music',
  icon: MusicIcon,
  label: 'Sheet Music'
}, {
  to: '/messages',
  icon: MessageSquareIcon,
  label: 'Messages'
}];
export const StudentLayout: React.FC<StudentLayoutProps> = ({
  children
}) => {
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  // Detect messages page to remove padding for edge-to-edge messaging layout
  const isMessagesPage = location.pathname === '/messages';
  // Derived state
  const isExpanded = isPinned || isHovered;
  // Track screen size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileMenuOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const handleMouseEnter = useCallback(() => {
    if (!isMobile) setIsHovered(true);
  }, [isMobile]);
  const handleMouseLeave = useCallback(() => {
    if (!isMobile) setIsHovered(false);
  }, [isMobile]);
  const togglePin = useCallback(() => {
    setIsPinned(prev => !prev);
  }, []);
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);
  return <div className={`w-full min-h-dvh ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white' : 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800'} font-sans transition-colors duration-300`} data-id="element-1616">
      <Navbar setIsDeleteAccountModalOpen={() => {}} data-id="element-1617" />

      <div className={`pt-20 ${isMessagesPage ? 'pb-0' : 'pb-16'} flex flex-col lg:flex-row`} data-id="element-1618">
        {/* Mobile sidebar toggle — hidden on Messages page to avoid overlapping Send button */}
        {!isMessagesPage && <button onClick={toggleMobileMenu} className="lg:hidden fixed bottom-4 right-4 z-30 bg-deep-pink text-white p-4 rounded-full shadow-lg" aria-label={isMobileMenuOpen ? 'Close sidebar' : 'Open sidebar'} style={{
        minWidth: '56px',
        minHeight: '56px'
      }} data-id="element-1619">
            {isMobileMenuOpen ? <XIcon size={24} data-id="element-1620" /> : <MenuIcon size={24} data-id="element-1621" />}
          </button>}

        {/* Desktop Sidebar */}
        <aside onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={`hidden lg:flex flex-col fixed inset-y-0 left-0 pt-20 pb-4 ${isDark ? 'bg-navy-900/95' : 'bg-white border-r border-gray-200/60'} shadow-lg z-20 transition-all duration-300 ease-in-out overflow-hidden`} style={{
        width: isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH
      }} data-id="element-1622">
          {/* Nav items */}
          <nav className="flex-1 px-2 py-6" data-id="element-1623">
            <ul className="space-y-1" data-id="element-1624">
              {navItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to;
              return <li key={item.to} data-id="element-1625">
                    <Link to={item.to} className={`flex items-center rounded-lg font-medium transition-colors ${isExpanded ? 'px-4 py-3' : 'px-0 py-3 justify-center'} ${isActive ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'}`} title={!isExpanded ? item.label : undefined} data-id="element-1626">
                      <Icon className={`h-5 w-5 flex-shrink-0 ${isExpanded ? '' : ''}`} data-id="element-1627" />
                      <span className={`text-sm whitespace-nowrap transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'ml-3 opacity-100 max-w-[180px]' : 'ml-0 opacity-0 max-w-0'}`} data-id="element-1628">
                        {item.label}
                      </span>
                    </Link>
                  </li>;
            })}
            </ul>
          </nav>

          {/* Pin toggle button */}
          <div className="px-2 pb-2" data-id="element-1629">
            <button onClick={togglePin} className={`flex items-center w-full rounded-lg py-2.5 transition-colors ${isExpanded ? 'px-4' : 'px-0 justify-center'} ${isDark ? 'text-gray-400 hover:text-white hover:bg-navy-800/70' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`} title={isPinned ? 'Collapse sidebar' : 'Pin sidebar open'} data-id="element-1630">
              {isPinned ? <ChevronsLeftIcon className="h-4 w-4 flex-shrink-0" data-id="element-1631" /> : <ChevronsRightIcon className="h-4 w-4 flex-shrink-0" data-id="element-1632" />}
              <span className={`text-xs whitespace-nowrap transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'ml-3 opacity-100 max-w-[180px]' : 'ml-0 opacity-0 max-w-0'}`} data-id="element-1633">
                {isPinned ? 'Collapse' : 'Pin sidebar'}
              </span>
            </button>
          </div>
        </aside>

        {/* Mobile Sidebar (drawer) */}
        <aside className={`lg:hidden fixed inset-y-0 left-0 pt-20 pb-16 w-64 ${isDark ? 'bg-navy-900/95' : 'bg-white'} shadow-lg z-20 transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`} data-id="element-1634">
          <nav className="px-4 py-6" data-id="element-1635">
            <ul className="space-y-3" data-id="element-1636">
              {navItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to;
              return <li key={item.to} data-id="element-1637">
                    <Link to={item.to} className={`flex items-center px-4 py-4 ${isActive ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => setIsMobileMenuOpen(false)} data-id="element-1638">
                      <Icon className="h-6 w-6 mr-3" data-id="element-1639" />
                      <span className="text-base" data-id="element-1640">{item.label}</span>
                    </Link>
                  </li>;
            })}
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 transition-all duration-300 ease-in-out px-4 ${isMessagesPage ? 'py-0' : 'py-6'}`} style={{
        marginLeft: isMobile ? 0 : isPinned ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        minHeight: isMessagesPage ? 'calc(100svh - 80px)' : 'calc(100svh - 144px)'
      }} data-id="element-1641">
          {children}
        </main>
      </div>

      <Footer data-id="element-1642" />

      {/* Mobile overlay */}
      {isMobileMenuOpen && <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-10" onClick={toggleMobileMenu} aria-hidden="true" data-id="element-1643" />}
    </div>;
};